import { useMemo } from 'react'
import { useQuery } from 'apollo-client'
import dayjs from 'dayjs'
import { useFt } from 'hooks'
import { constants } from 'helpers'
import { priceChannels } from 'modules/priceSelection'
import { useUser } from 'modules/user'

import limitedDropSaleQuery from './graph/limitedDropSale.graphql'


type UseLimitedDropSaleProps = {
  id?: string
  detailed?: boolean
  skip?: boolean
}

const analyticEventStatusMap = {
  LIVE: 'Active',
  SOLD_OUT: 'Sold out',
  FINISHED: 'Ended',
}

const useLimitedDropSale = (props?: UseLimitedDropSaleProps) => {
  const { id, detailed = false, skip } = props || {}
  const isExactLimitedDropSale = Boolean(id)
  const isLimitedDropEnabled = useFt(constants.features.limitedDrops)
  const { country } = useUser()

  const { data: result, isFetching, error } = useQuery(limitedDropSaleQuery, {
    fetchPolicy: 'cache-first',
    skip: skip || !isLimitedDropEnabled,
    variables: {
      uid: id || '',
      priceSelection: {
        country,
        channel: priceChannels.limitedDropSale,
      },
      detailed,
      exact: isExactLimitedDropSale,
    },
  })

  const modifiedData = useMemo<LimitedDropSale.Payload>(() => {
    const data = isExactLimitedDropSale ? result?.limitedDropSale?.data : result?.limitedDropSaleCurrent?.data

    if (!data) {
      return null
    }

    const now = dayjs()

    // Fallback in case there is a company without specifying a burndown date
    const burndownDate = data.burndownDate || data.endDate

    /*
    * The general promotion lasts from startDate to endDate
    * During this period, the user can add products to the cart and get to the Limited Drops landing page
    * The burndownDate determines the final period until which the priority of entry points is valid for this promotion (a timer is shown, a modal window opens)
    * If the burndownDate is not defined, then its role is played by endDate
    * */
    const totalExpirationTime = dayjs(data.endDate).diff(now)
    const burndownExpirationTime = dayjs(burndownDate).diff(now)
    const hoursUntilBurndown = dayjs(burndownDate).diff(data.startDate, 'hours')
    const hasReachedBurndown = dayjs(burndownDate).tz('America/New_York').isBefore(now)
    const isSoldOut = data.status === 'SOLD_OUT' || data.status === 'FINISHED' || totalExpirationTime <= 0

    const metadata: LimitedDropSale.Metadata = { productIds: [], productBrands: [], productFullNames: [], limitedDropStatus: analyticEventStatusMap[data.status] }

    data.products?.forEach((product) => {
      const { id, brandInfo, fullName } = product.productInfo
      const isPartiallySoldOut = !isSoldOut && product.limitedEditionInfo?.productsRemainingCount === 0

      metadata.productIds.push(id)
      metadata.productBrands.push(brandInfo.name)
      metadata.productFullNames.push(fullName)

      metadata.limitedDropStatus = isPartiallySoldOut ? 'Partially sold-out' : metadata.limitedDropStatus
    })

    return {
      ...data,
      isSoldOut,
      metadata,
      hoursUntilBurndown,
      hasReachedBurndown,
      expirationTime: burndownExpirationTime,
      isCurrentSale: !isExactLimitedDropSale,
    }
  }, [ result, isExactLimitedDropSale ])

  return {
    data: modifiedData,
    error,
    isFetching,
  }
}

export default useLimitedDropSale
